import { gql } from "@apollo/client";
import {
  Flex,
  FormControl,
  FormLabel,
  Select,
  Skeleton,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { FC, useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { QUESTION_TYPE_TEXT } from "src/components/Form/constants";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { useEditFormTemplateContext } from "../../../../context";
import { FormikType } from "./formik";

export type Props = {
  questionId: uuid;
  sectionType: AF.V2.SectionType;
  isDisabled?: boolean;
  error?: string | undefined;
};

type QuestionTypeOption = {
  label: string;
  value: string;
};

/**
 * There is a tight coupling between our static AF.V2.QuestionType and our Avela Form framework.
 * Custom Question Types (CQTs) fit into that tight coupling by creating an additional type
 * AF.V2.QuestionTypes.CustomQuestion ("CustomQuestion").
 *
 * CQTs are user defined and so we navigate the tight coupling of static question types by
 * using AF.V2.QuestionTypes.CustomQuestion as a shim to assign or determine the CQT associated with a question.
 */
const BASE_QUESTION_TYPE_OPTIONS = AF.V2.QuestionTypeList.filter((type) => {
  // AF.V2.QuestionTypes.CustomQuestion is not displayed to the user
  return type !== AF.V2.QuestionTypes.CustomQuestion;
}).map((type) => {
  return { label: QUESTION_TYPE_TEXT[type].label, value: type };
});

export const QuestionType: FC<Props> = ({ error, questionId, sectionType }) => {
  const formik = useFormikContext<FormikType>();
  const organization = useOrganization();

  const baseQuestionTypeOptions = useMemo(() => {
    if (sectionType === AF.V2.SectionTypes.PreRankingSection) {
      return BASE_QUESTION_TYPE_OPTIONS;
    }

    return BASE_QUESTION_TYPE_OPTIONS.filter((type) => {
      return type.value !== AF.V2.QuestionTypes.Grades;
    });
  }, [sectionType]);

  const { remoteData } = useRemoteDataQuery<
    GQL.GetCustomQuestionTypesByOrganization,
    GQL.GetCustomQuestionTypesByOrganizationVariables
  >(
    gql`
      query GetCustomQuestionTypesByOrganization($organizationId: uuid!) {
        custom_question_type(
          where: { organization_id: { _eq: $organizationId } }
        ) {
          id
          name
          custom_question_type_fields(order_by: { question: { order: asc } }) {
            question_id
            question {
              id
              type
              is_required
              key
              link_text
              link_url
              order
              permission_level
              question
              form_question {
                category
                form_question_options {
                  id
                  label
                  order
                  translate_options
                  value
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        organizationId: organization.toNullable()?.id ?? "",
      },
      fetchPolicy: "cache-first",
      skip: !organization.hasData(),
    }
  );

  remoteData
    .map(({ custom_question_type }) =>
      custom_question_type.map((cqt) => ({
        label: cqt.name,
        value: cqt.id,
      }))
    )
    .mapError((error) => {
      return new Error("GraphQL error", { cause: error });
    });

  const id = `type-${questionId}`;

  const context = useEditFormTemplateContext();
  const canEditQuestionType = context.helpers.isUnpublishedQuestion(questionId);

  return (
    <RemoteDataView
      remoteData={remoteData}
      error={(e) => <GenericError error={e} />}
      loading={<Loading />}
    >
      {({ custom_question_type }) => {
        const customQuestionTypeOptions: QuestionTypeOption[] =
          custom_question_type.map((cqt) => ({
            label: cqt.name,
            value: cqt.id,
          }));

        const questionTypeOptions = [
          ...baseQuestionTypeOptions,
          ...customQuestionTypeOptions,
        ];

        return (
          <Flex direction="column" gap="5">
            <FormControl isInvalid={error !== undefined}>
              <FormLabel htmlFor={id}>Question type</FormLabel>
              <Select
                isDisabled={!canEditQuestionType}
                name="type"
                id={id}
                defaultValue={
                  formik.values.type === AF.V2.QuestionTypes.CustomQuestion
                    ? formik.values.customQuestionTypeId
                    : formik.values.type
                }
                onChange={async (event) => {
                  const value = event.target.value as AF.V2.QuestionType;

                  // Check if it's a standard question type from the enum
                  if (AF.V2.QuestionTypeList.includes(value)) {
                    await formik.setFieldValue("type", value);
                    await formik.setFieldValue(
                      "customQuestionTypeId",
                      undefined
                    );
                  } else {
                    await formik.setFieldValue(
                      "type",
                      AF.V2.QuestionTypes.CustomQuestion
                    );
                    await formik.setFieldValue("customQuestionTypeId", value);
                  }
                }}
              >
                {questionTypeOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap={2}>
      <Skeleton height="1.3rem" width="10rem" />
      <Skeleton height="2.5rem" width="100%" />
    </Flex>
  );
};
